import React, { FC } from 'react';

import {
  FCOFields,
  FCOType,
  FeatureForFCO,
  FeatureServiceFCO,
  FeatureServiceFCOFields,
  FeatureViewFCO,
  FeatureViewFCOFields,
} from '../../../core/types/fcoTypes';
import { EnabledDisabledBadge, RelatedFCOs } from '@tecton/ComponentRedesign';

import { Card, Table, ExtendedTableColumn, Link, Text, FlexGroup, FlexItem, Avatar } from '@lithos';
import { dateTimeFormat, timeZone } from '@lithos/DateUtils';

interface FeatureServiceFeatureTableProps {
  fco: FeatureServiceFCO;
  fcos: any; // from useGetFcos,
  workspace: string;
}

interface FeatureRow {
  feature: FeatureForFCO;
  featureView: FeatureViewFCO;
}

const FeatureServiceFeatureTable: FC<FeatureServiceFeatureTableProps> = ({ fco, fcos, workspace }) => {
  const dependentFeatureViews = fco[FeatureServiceFCOFields.ALL_FEATURE_VIEWS].map((featureViewId) => {
    return fcos.featureViews.find((featureView: FeatureViewFCO) => featureView.id === featureViewId);
  });

  const features: FeatureRow[] = [];

  // This filters all possible feature views for only the feature views served by the Feature Service, not just the potential features available to it
  // This is ONLY necessary because raw FeatureService objects don't include important info (i.e. the type), just the column name
  // We could address this in GraphQL in the future
  dependentFeatureViews.forEach((featureView: FeatureViewFCO) => {
    featureView[FeatureViewFCOFields.FEATURES].forEach((feature) => {
      if (
        fco[FeatureServiceFCOFields.FEATURES_SERVED].some(
          (served) => served.featureName === feature.name && served.featureViewId === String(featureView.id)
        )
      ) {
        features.push({
          feature: feature,
          featureView: featureView,
        });
      }
    });
  });

  const columns: ExtendedTableColumn<FeatureRow & { id: string }>[] = [
    {
      name: 'Feature',
      content: (item: FeatureRow) => <>{item.feature.name}</>,
      id: 'feature',
      columnWidth: 'l',
    },
    {
      name: 'Feature View or Table',
      id: 'featureView',
      content: (item: FeatureRow) => (
        <Link
          url={`/repo/${workspace}/features/${item.featureView.name}/dataflow`}
          name={item.featureView.name ?? ''}
          icon="FeatureView"
        />
      ),
      columnWidth: 'l',
    },
    {
      name: 'Entities',
      id: 'entities',
      content: (item: FeatureRow) => {
        if (!item.featureView) {
          return <></>;
        }
        const entities = item.featureView[FeatureViewFCOFields.ENTITY_IDS].map((id: string) => {
          return fcos.idToFcoMap[id];
        });

        return <RelatedFCOs fcos={entities} type={FCOType.ENTITY} workspace={workspace} />;
      },
      columnWidth: 's',
    },
    {
      name: 'Type',
      id: 'type',
      content: (item: FeatureRow) => <Text code>{item.feature.type}</Text>,
    },
    {
      name: 'Online',
      id: 'online',
      columnWidth: 's',
      content: (item: FeatureRow) => (
        <>
          {item.featureView[FeatureViewFCOFields.IS_REALTIME] ? (
            <>n/a</>
          ) : (
            <EnabledDisabledBadge
              enabled={item.featureView[FeatureViewFCOFields.IS_ONLINE_MATERIALIZATION_ENABLED] ?? false}
            />
          )}
        </>
      ),
    },
    {
      name: 'Offline',
      id: 'offline',
      columnWidth: 's',
      content: (item: FeatureRow) => (
        <>
          {item.featureView[FeatureViewFCOFields.IS_REALTIME] ? (
            <>n/a</>
          ) : (
            <EnabledDisabledBadge
              enabled={item.featureView[FeatureViewFCOFields.IS_OFFLINE_MATERIALIZATION_ENABLED] ?? false}
            />
          )}
        </>
      ),
    },
    {
      name: 'Owner',
      id: 'owner',
      columnWidth: 'l',
      content: (item: FeatureRow) => (
        <FlexGroup>
          <FlexItem grow="0" shrink="0">
            <Avatar name={item.featureView[FCOFields.OWNER] ?? ''} />
          </FlexItem>
          <FlexItem grow="0" shrink="0">
            {item.featureView[FCOFields.OWNER]}
          </FlexItem>
        </FlexGroup>
      ),
    },
    {
      name: 'Created',
      id: 'created',
      content: (item: FeatureRow) =>
        item.featureView[FCOFields.CREATED_DATE] ? (
          dateTimeFormat(item.featureView[FCOFields.CREATED_DATE], timeZone.UTC)
        ) : (
          <>n/a</>
        ),
      columnWidth: 'l',
    },
  ];

  return (
    <Card title={`Features (${features.length})`}>
      <Table
        items={features.map((feature) => ({
          id: `${feature.featureView.id}__${feature.feature.name}`,
          ...feature,
        }))}
        columns={columns}
        frozenFirstColumn
      />
    </Card>
  );
};

export default FeatureServiceFeatureTable;
